import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

// Always a readable string (FastAPI 422 details are arrays of objects).
const errorText = (e, fallback) => {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => `${(x.loc || []).slice(-1)[0] || 'field'}: ${x.msg}`).join('; ');
  if (!e?.response) return `${fallback}: network error or timeout — please retry`;
  return `${fallback} (HTTP ${e.response.status})`;
};
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import {
  X, Send, Mail, Eye, Link2, Loader2, FileText, CheckCircle, Clock, XCircle, AlertCircle, Edit,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS = {
  draft: { label: 'Draft', cls: 'bg-gray-100 text-gray-700', icon: Edit },
  sent: { label: 'Sent', cls: 'bg-blue-100 text-blue-700', icon: Send },
  in_progress: { label: 'In Progress', cls: 'bg-amber-100 text-amber-700', icon: Clock },
  completed: { label: 'Completed', cls: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  cancelled: { label: 'Cancelled', cls: 'bg-red-100 text-red-700', icon: XCircle },
  expired: { label: 'Expired', cls: 'bg-gray-100 text-gray-500', icon: AlertCircle },
};

const PENDING_STATES = ['sent', 'in_progress'];

export default function SentDocumentsModal({ template, onClose }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState('');
  const [reminding, setReminding] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailMsg, setEmailMsg] = useState('');
  const [includeLinks, setIncludeLinks] = useState(false);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/esignature/requests`, {
        headers, params: { template_id: template.id },
      });
      setRequests(res.data || []);
    } catch (e) {
      toast.error('Failed to load sent documents');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template.id]);

  useEffect(() => { load(); }, [load]);

  const summary = useMemo(() => {
    const s = { total: requests.length, completed: 0, pending: 0, other: 0 };
    requests.forEach(r => {
      if (r.status === 'completed') s.completed += 1;
      else if (PENDING_STATES.includes(r.status)) s.pending += 1;
      else s.other += 1;
    });
    return s;
  }, [requests]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return requests;
    return requests.filter(r => {
      const names = (r.signers || []).map(s => `${s.name || ''} ${s.email || ''}`).join(' ').toLowerCase();
      return (r.name || '').toLowerCase().includes(q) || names.includes(q);
    });
  }, [requests, search]);

  const allSelected = filtered.length > 0 && filtered.every(r => selected.has(r.id));
  const toggleAll = () => {
    setSelected(prev => {
      const next = new Set(prev);
      if (allSelected) filtered.forEach(r => next.delete(r.id));
      else filtered.forEach(r => next.add(r.id));
      return next;
    });
  };
  const toggleOne = (id) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const selectedIds = Array.from(selected);

  const handleRemind = async () => {
    if (selectedIds.length === 0) return;
    setReminding(true);
    try {
      const res = await axios.post(`${API}/esignature/requests/bulk-remind`, { request_ids: selectedIds }, { headers });
      const n = res.data?.reminded_signers ?? 0;
      const skipped = (res.data?.skipped || []).length;
      const failures = res.data?.failures || [];
      if (n > 0) toast.success(`Reminder sent to ${n} pending signer(s).` + (skipped ? ` ${skipped} had none pending.` : ''));
      else if (!failures.length) toast.info(skipped ? `Nothing to remind — ${skipped} selected document(s) have no pending signers.` : 'No reminders sent.');
      if (failures.length) {
        toast.error(`${failures.length} reminder(s) not sent`, { description: failures.slice(0, 6).join(' • ') + (failures.length > 6 ? ` • +${failures.length - 6} more` : ''), duration: 12000 });
      }
      load();
    } catch (e) {
      toast.error(errorText(e, 'Failed to send reminders'));
    } finally {
      setReminding(false);
    }
  };

  const handleEmailExternal = async () => {
    if (selectedIds.length === 0) return;
    if (!emailTo.trim() || !emailTo.includes('@')) { toast.error('Enter a valid email address'); return; }
    setEmailing(true);
    try {
      const res = await axios.post(`${API}/esignature/requests/email-external`, {
        request_ids: selectedIds,
        to_email: emailTo.trim(),
        message: emailMsg.trim() || null,
        include_signing_links: includeLinks,
      }, { headers });
      toast.success(`Emailed ${res.data?.listed ?? selectedIds.length} document(s) to ${emailTo.trim()} (${res.data?.attached ?? 0} PDF attached).`);
      setShowEmail(false); setEmailTo(''); setEmailMsg(''); setIncludeLinks(false);
    } catch (e) {
      toast.error(errorText(e, 'Failed to send email'));
    } finally {
      setEmailing(false);
    }
  };

  const viewPdf = async (r) => {
    try {
      const res = await axios.get(`${API}/esignature/requests/${r.id}/completed-pdf`, { headers, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank');
    } catch (e) {
      toast.error('Signed PDF not available');
    }
  };

  const copyLink = (r) => {
    const pend = (r.signers || []).find(s => (s.status === 'pending' || s.status === 'viewed') && s.signing_token);
    if (!pend) { toast.info('No pending signing link for this document'); return; }
    const link = `${window.location.origin}/sign/${r.id}?token=${pend.signing_token}`;
    navigator.clipboard.writeText(link);
    toast.success('Signing link copied');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="sent-docs-modal">
      <div className="bg-background rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h2 className="text-lg font-semibold">Sent Documents</h2>
            <p className="text-sm text-muted-foreground">{template.name}</p>
          </div>
          <button onClick={onClose} data-testid="sent-docs-close" className="p-1 rounded hover:bg-muted"><X className="w-5 h-5" /></button>
        </div>

        {/* Summary */}
        <div className="flex flex-wrap gap-2 px-4 py-3 border-b text-sm" data-testid="sent-docs-summary">
          <span className="px-2 py-1 rounded bg-muted">{summary.total} total</span>
          <span className="px-2 py-1 rounded bg-emerald-100 text-emerald-700">{summary.completed} completed</span>
          <span className="px-2 py-1 rounded bg-amber-100 text-amber-700">{summary.pending} pending</span>
          {summary.other > 0 && <span className="px-2 py-1 rounded bg-gray-100 text-gray-600">{summary.other} other</span>}
        </div>

        {/* Search + bulk actions */}
        <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b">
          <Input placeholder="Search by name or email…" value={search} onChange={e => setSearch(e.target.value)} className="max-w-xs" data-testid="sent-docs-search" />
          <div className="flex-1" />
          <span className="text-sm text-muted-foreground">{selectedIds.length} selected</span>
          <Button size="sm" variant="outline" disabled={selectedIds.length === 0 || reminding} onClick={handleRemind} data-testid="sent-docs-remind-btn">
            {reminding ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />} Remind pending
          </Button>
          <Button size="sm" disabled={selectedIds.length === 0} onClick={() => setShowEmail(v => !v)} data-testid="sent-docs-email-btn">
            <Mail className="w-4 h-4 mr-1" /> Email documents
          </Button>
        </div>

        {/* External email form */}
        {showEmail && (
          <div className="px-4 py-3 border-b bg-muted/40 space-y-2" data-testid="sent-docs-email-form">
            <p className="text-sm font-medium">Email {selectedIds.length} document(s) to an external recipient (e.g. agency partner)</p>
            <div className="flex flex-wrap gap-2">
              <Input type="email" placeholder="recipient@agency.com" value={emailTo} onChange={e => setEmailTo(e.target.value)} className="max-w-xs" data-testid="sent-docs-email-to" />
              <Input placeholder="Optional message…" value={emailMsg} onChange={e => setEmailMsg(e.target.value)} className="flex-1 min-w-[200px]" data-testid="sent-docs-email-msg" />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={includeLinks} onCheckedChange={v => setIncludeLinks(!!v)} data-testid="sent-docs-include-links" />
              Include signing links for pending documents
            </label>
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="ghost" onClick={() => setShowEmail(false)}>Cancel</Button>
              <Button size="sm" disabled={emailing} onClick={handleEmailExternal} data-testid="sent-docs-email-send">
                {emailing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Mail className="w-4 h-4 mr-1" />} Send
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">Completed documents attach as signed PDFs. Replies route to your email.</p>
          </div>
        )}

        {/* List */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <FileText className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p>No documents have been sent from this template yet.</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-background border-b">
                <tr>
                  <th className="p-2 w-8"><Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="sent-docs-select-all" /></th>
                  <th className="p-2 text-left">Recipient(s)</th>
                  <th className="p-2 text-left">Status</th>
                  <th className="p-2 text-left">Sent</th>
                  <th className="p-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => {
                  const st = STATUS[r.status] || STATUS.draft;
                  const Icon = st.icon;
                  const isCompleted = r.status === 'completed';
                  const isPending = PENDING_STATES.includes(r.status);
                  const when = r.created_at ? new Date(r.created_at).toLocaleDateString() : '—';
                  return (
                    <tr key={r.id} className="border-b hover:bg-muted/40" data-testid={`sent-docs-row-${r.id}`}>
                      <td className="p-2"><Checkbox checked={selected.has(r.id)} onCheckedChange={() => toggleOne(r.id)} data-testid={`sent-docs-check-${r.id}`} /></td>
                      <td className="p-2">
                        <div className="font-medium">{r.name || 'Document'}</div>
                        <div className="text-xs text-muted-foreground">
                          {(r.signers || []).map(s => `${s.name || s.email}${s.status === 'signed' ? ' ✓' : ''}`).join(', ') || '—'}
                        </div>
                      </td>
                      <td className="p-2">
                        <Badge className={`${st.cls} hover:${st.cls} gap-1`}><Icon className="w-3 h-3" />{st.label}</Badge>
                      </td>
                      <td className="p-2 text-muted-foreground">{when}</td>
                      <td className="p-2">
                        <div className="flex justify-end gap-1">
                          {isCompleted && (
                            <Button size="sm" variant="ghost" onClick={() => viewPdf(r)} title="View signed PDF" data-testid={`sent-docs-view-${r.id}`}><Eye className="w-4 h-4" /></Button>
                          )}
                          {isPending && (
                            <Button size="sm" variant="ghost" onClick={() => copyLink(r)} title="Copy signing link" data-testid={`sent-docs-copy-${r.id}`}><Link2 className="w-4 h-4" /></Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
